/**
 * ContentFilterBar - Filter bar for content pages (Shots, Workflows, Cast, Assets)
 * Sits below the ProjectTitleBar but only spans the content area
 * Layout: [Sort | Filters] on left, [Search | Add] on right
 */

import { useState, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpDown,
  SlidersHorizontal,
  Plus,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchBox } from "@/components/dashboard/SearchBox";
import { AdvancedFiltersContent } from "@/components/dashboard/AdvancedFilters";
import { CastCreateDialog } from "@/components/cast/CastCreateDialog";
import {
  PLACEHOLDER_CASTS,
  CAST_TYPE_COLORS,
} from "@/components/cast/CastContainer";
import { useSearch } from "@/hooks/useSearch";
import { SORT_OPTIONS } from "@/constants/search";
import { useScenes, useShotDetail } from "@/hooks/shots";
import "@/components/shared/FilterButton.css";

// Routes where the filter bar should be visible
const FILTER_BAR_ROUTES = [
  /^\/productions\/[^/]+\/scenes$/, // Scenes (new section)
  /^\/productions\/[^/]+\/workflows$/, // Workflows
  /^\/productions\/[^/]+\/cast$/, // Cast list (minimal bar, tabs are inline)
  /^\/productions\/[^/]+\/cast\/[^/]+$/, // Cast detail
  /^\/productions\/[^/]+\/assets$/, // Assets
  // Note: Audio page uses its own AudioFilterPanel, not ContentFilterBar
];

// Pattern to detect cast list pages (show minimal bar since tabs are inline)
const CAST_LIST_PATTERN = /^\/productions\/[^/]+\/cast$/;

// Pattern to detect cast detail pages
const CAST_DETAIL_PATTERN = /^\/productions\/([^/]+)\/cast\/([^/]+)$/;

// Pattern to detect scenes list pages
const SCENES_LIST_PATTERN = /^\/productions\/[^/]+\/scenes$/;

export function ContentFilterBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId } = useParams<{ projectId: string }>();
  const search = useSearch(projectId ?? null);

  const [isSortOpen, setIsSortOpen] = useState(false);

  // Check if current route should show the filter bar
  const shouldShow = FILTER_BAR_ROUTES.some((pattern) =>
    pattern.test(location.pathname),
  );

  // Check if we're on a cast detail page
  const castDetailMatch = location.pathname.match(CAST_DETAIL_PATTERN);
  const isCastDetail = !!castDetailMatch;

  // Check if we're on cast list page
  const isCastList = CAST_LIST_PATTERN.test(location.pathname);

  // Check if we're on scenes list page
  const isShotsList = SCENES_LIST_PATTERN.test(location.pathname);

  // Check if viewing a single shot (has ?shot= param on shots page)
  const selectedShotId = searchParams.get("shot");
  const isShotDetail = isShotsList && !!selectedShotId;

  // Get scenes data from the shared Shot Builder query (same cache key as
  // ShotBuilder, so mutations there refresh this header automatically). Only
  // fetched on the shots list page.
  const { data: scenesData = [] } = useScenes(
    isShotsList ? (projectId ?? undefined) : undefined,
  );

  // Fetch shot detail to get workflowId (list shots are "thin" without workflow)
  const { data: shotDetail } = useShotDetail(
    isShotDetail ? (selectedShotId ?? undefined) : undefined,
  );

  // Get scene tabs for shots list
  const sceneTabs = useMemo(() => {
    if (!isShotsList) return [];
    return scenesData.map((scene, index) => ({
      id: scene.id,
      label: `${index + 1}. ${scene.name}`,
    }));
  }, [isShotsList, scenesData]);

  // Get selected shot and its scene (for shot detail view)
  const { selectedShot, selectedShotScene } = useMemo(() => {
    if (!isShotDetail || !selectedShotId) {
      return { selectedShot: null, selectedShotScene: null };
    }
    for (const scene of scenesData) {
      const shot = scene.shots.find((s) => s.id === selectedShotId);
      if (shot) {
        return { selectedShot: shot, selectedShotScene: scene };
      }
    }
    return { selectedShot: null, selectedShotScene: null };
  }, [isShotDetail, selectedShotId, scenesData]);

  // Get selected cast (for cast detail view)
  const selectedCast = useMemo(() => {
    if (!isCastDetail || !castDetailMatch) return null;
    const castId = castDetailMatch[2]; // Extract cast ID from URL
    return PLACEHOLDER_CASTS[castId] || null;
  }, [isCastDetail, castDetailMatch]);

  // Early return AFTER all hooks
  if (!shouldShow) {
    return null;
  }

  // Get current scene tab from URL
  const currentSceneTab = searchParams.get("scene") || "all";

  // Determine placeholder and add button text based on current route
  const isShots = location.pathname.endsWith("/scenes");
  const isWorkflows = location.pathname.endsWith("/workflows");
  const isCastPage = location.pathname.endsWith("/cast");
  const isAssets = location.pathname.endsWith("/assets");

  const handleBackToCast = () => {
    if (projectId) {
      navigate(`/productions/${projectId}/cast`);
    }
  };

  const handleBackToShots = () => {
    setSearchParams((prev) => {
      prev.delete("shot");
      prev.delete("tab");
      return prev;
    });
  };

  const handleOpenWorkflow = () => {
    // Navigate to the workflow editor for this shot
    // Use shotDetail for workflowId since list shots don't include it
    const workflowId = shotDetail?.workflowId;
    if (workflowId) {
      navigate(`/workflows/${workflowId}`);
    }
  };

  const handleSceneTabChange = (sceneId: string) => {
    if (sceneId === "all") {
      searchParams.delete("scene");
    } else {
      searchParams.set("scene", sceneId);
    }
    setSearchParams(searchParams);
  };

  const getPlaceholder = () => {
    if (isShots) return "Search shots...";
    if (isWorkflows) return "Search workflows...";
    if (isCastPage) return "Search cast...";
    if (isAssets) return "Search assets...";
    return "Search...";
  };

  const getAddButtonText = () => {
    if (isShots) return "Add Scene";
    if (isWorkflows) return "Add workflow";
    if (isCastPage) return "Add cast";
    if (isAssets) return "Add asset";
    return "Add";
  };

  const handleAddClick = () => {
    if (isWorkflows) {
      // Use URL-based modal triggering
      setSearchParams({ modal: "create-workflow" });
    } else if (isShots) {
      // Use URL-based modal triggering for shots
      setSearchParams({ modal: "create-scene" });
    }
    // Cast and Assets add functionality can be added later
  };

  // Close filter panel when sort dropdown opens
  const handleSortOpenChange = (open: boolean) => {
    setIsSortOpen(open);
    if (open && search.isAdvancedOpen) {
      search.setIsAdvancedOpen(false);
    }
  };

  // Close filter panel when search is focused
  const handleSearchFocus = () => {
    if (search.isAdvancedOpen) {
      search.setIsAdvancedOpen(false);
    }
  };

  const handleAdvancedToggle = () => {
    search.setIsAdvancedOpen(!search.isAdvancedOpen);
  };

  // Check if cast create dialog should be open (URL-based)
  const isCastDialogOpen = searchParams.get("modal") === "create-cast";

  const handleOpenCastDialog = () => {
    searchParams.set("modal", "create-cast");
    setSearchParams(searchParams);
  };

  const handleCloseCastDialog = () => {
    searchParams.delete("modal");
    setSearchParams(searchParams);
  };

  // On shot detail page, render back button + shot title + scene name + Open Workflow CTA
  if (isShotDetail && selectedShot && selectedShotScene) {
    return (
      <div
        className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0"
        style={{
          backgroundColor: "var(--color-panel)",
          borderBottomColor: "var(--color-panel-border)",
        }}
      >
        {/* Left: Back button + Shot title + Scene name */}
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="utility"
            size="icon"
            onClick={handleBackToShots}
            aria-label="Back to shots"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-base font-semibold text-foreground truncate"
              style={{ fontSize: "16px" }}
            >
              {selectedShot.name}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-sm text-muted-foreground truncate">
              {selectedShotScene.name}
            </span>
          </div>
        </div>

        {/* Right: Status indicator + Open Workflow CTA */}
        <div className="flex items-center gap-4">
          {/* Status indicator */}
          <div className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full"
              style={{
                backgroundColor:
                  selectedShot.status === "approved"
                    ? "var(--color-editor-status-approved)"
                    : selectedShot.status === "in-progress"
                      ? "var(--color-primitive-amber-600)"
                      : selectedShot.status === "ready-for-review"
                        ? "var(--color-primitive-blue-500)"
                        : "var(--color-editor-text-muted)",
              }}
            />
            <span className="text-sm text-muted-foreground capitalize">
              {selectedShot.status.replace("-", " ")}
            </span>
          </div>

          {shotDetail?.workflowId && (
            <button
              type="button"
              onClick={handleOpenWorkflow}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring px-4 text-muted-foreground hover:text-foreground"
              style={{
                height: "var(--btn-md)",
                background: "rgba(var(--color-primitive-neutral-0-rgb), 0.05)",
                border:
                  "1px solid rgba(var(--color-primitive-neutral-0-rgb), 0.12)",
                borderRadius: "var(--radius-default)",
                gap: "6px",
                cursor: "pointer",
                transition: "all 0.15s ease",
                fontSize: "13px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "rgba(var(--color-primitive-neutral-0-rgb), 0.1)";
                e.currentTarget.style.borderColor =
                  "rgba(var(--color-primitive-neutral-0-rgb), 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "rgba(var(--color-primitive-neutral-0-rgb), 0.05)";
                e.currentTarget.style.borderColor =
                  "rgba(var(--color-primitive-neutral-0-rgb), 0.12)";
              }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Workflow
            </button>
          )}
        </div>
      </div>
    );
  }

  // Fallback: Shot is selected but data not loaded yet - show simple back button
  if (isShotDetail && (!selectedShot || !selectedShotScene)) {
    return (
      <div
        className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0"
        style={{
          backgroundColor: "var(--color-panel)",
          borderBottomColor: "var(--color-panel-border)",
        }}
      >
        {/* Left: Back button */}
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="utility"
            size="icon"
            onClick={handleBackToShots}
            aria-label="Back to shots"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>

        {/* Right: All Scenes button */}
        <button
          type="button"
          onClick={handleBackToShots}
          className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring shadow-sm h-9 px-4"
          style={{
            backgroundColor: "transparent",
            border: "1px solid rgba(var(--color-primitive-neutral-0-rgb), 0.2)",
            color: "var(--color-primitive-neutral-200)",
            borderRadius: "var(--radius-default)",
            gap: "6px",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor =
              "rgba(var(--color-primitive-neutral-0-rgb), 0.05)";
            e.currentTarget.style.borderColor =
              "rgba(var(--color-primitive-neutral-0-rgb), 0.3)";
            e.currentTarget.style.color = "var(--color-primitive-neutral-0)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.borderColor =
              "rgba(var(--color-primitive-neutral-0-rgb), 0.2)";
            e.currentTarget.style.color = "var(--color-primitive-neutral-200)";
          }}
        >
          <ArrowLeft style={{ width: "16px", height: "16px" }} />
          All Scenes
        </button>
      </div>
    );
  }

  // On shots list page, render scene tabs + search + add button (no sort/filter)
  // Only show this when NOT viewing a single shot (no ?shot= param)
  if (isShotsList && !selectedShotId) {
    return (
      <div
        className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0"
        style={{
          backgroundColor: "var(--color-panel)",
          borderBottomColor: "var(--color-panel-border)",
        }}
      >
        {/* Left: Scene tabs */}
        <div
          className="project-tabs"
          style={{ borderBottom: "none", marginBottom: 0 }}
        >
          {/* All tab */}
          <button
            type="button"
            onClick={() => handleSceneTabChange("all")}
            className={`project-tab ${currentSceneTab === "all" ? "project-tab--active" : ""}`}
          >
            All
          </button>
          {/* Scene tabs */}
          {sceneTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleSceneTabChange(tab.id)}
              className={`project-tab ${currentSceneTab === tab.id ? "project-tab--active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Right: Search + New Scene button */}
        <div className="flex items-center gap-2">
          <SearchBox
            value={search.searchQuery}
            onChange={search.setSearchQuery}
            isSearching={search.isSearching}
            placeholder="Search shots..."
          />
          <Button
            variant="pink"
            type="button"
            onClick={handleAddClick}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring shadow-sm h-9 px-4"
            style={{
              borderRadius: "var(--radius-default)",
              gap: "6px",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <Plus style={{ width: "16px", height: "16px" }} />
            Add Scene
          </Button>
        </div>
      </div>
    );
  }

  // On cast list page with single cast selected (?cast=ID), show back + name
  const selectedCastIdFromParams = searchParams.get("cast");
  // Check if we have a real LoRA ID selected (even if not in placeholder data)
  const hasCastSelected = !!selectedCastIdFromParams;
  const selectedCastFromParams = selectedCastIdFromParams
    ? PLACEHOLDER_CASTS[selectedCastIdFromParams]
    : null;

  const handleBackToCastList = () => {
    setSearchParams((prev) => {
      prev.delete("cast");
      prev.delete("tab");
      return prev;
    });
  };

  // Tab definitions for cast detail view
  const CAST_TABS = [
    { id: "overview", label: "Overview" },
    { id: "loras", label: "LoRAs" },
    { id: "train", label: "Train LoRA" },
    { id: "scripts", label: "Script & Gaps", disabled: true },
    { id: "context", label: "Smart Context", disabled: true },
    { id: "history", label: "History", disabled: true },
  ];

  const currentCastTab = searchParams.get("tab") || "overview";

  const handleCastTabChange = (tabId: string) => {
    setSearchParams((prev) => {
      prev.set("tab", tabId);
      return prev;
    });
  };

  // On cast list page with a single cast selected - show back button + name + tabs
  // Use hasCastSelected to detect when ?cast= is present, even if cast data not in placeholders
  if (isCastList && hasCastSelected) {
    const typeColor = selectedCastFromParams
      ? CAST_TYPE_COLORS[selectedCastFromParams.type]
      : "var(--color-primitive-pink-500)"; // Default color for real LoRAs

    return (
      <div
        className="flex items-center px-6 py-3 border-b flex-shrink-0"
        style={{
          backgroundColor: "var(--color-panel)",
          borderBottomColor: "var(--color-panel-border)",
        }}
      >
        {/* Left: Back button + Cast name + Type */}
        <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
          <Button
            variant="utility"
            size="icon"
            onClick={handleBackToCastList}
            aria-label="Back to cast"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          {selectedCastFromParams ? (
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="text-base font-semibold text-foreground truncate"
                style={{ fontSize: "16px" }}
              >
                {selectedCastFromParams.name}
              </span>
              <span className="text-muted-foreground/50 text-lg leading-none">
                &middot;
              </span>
              <span
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: typeColor }}
              >
                {selectedCastFromParams.type}
              </span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground uppercase tracking-wide">
              Cast
            </span>
          )}
        </div>

        {/* Separator */}
        <div
          className="mx-4 h-6 w-px flex-shrink-0"
          style={{
            backgroundColor: "rgba(var(--color-primitive-neutral-0-rgb), 0.12)",
          }}
        />

        {/* Tabs */}
        <div
          className="project-tabs flex-1 min-w-0"
          style={{ borderBottom: "none", marginBottom: 0 }}
        >
          {CAST_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => !tab.disabled && handleCastTabChange(tab.id)}
              disabled={tab.disabled}
              className={`project-tab ${currentCastTab === tab.id ? "project-tab--active" : ""} ${tab.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // On cast list page (grid view), render search + add button (no tabs)
  if (isCastList) {
    return (
      <>
        <div
          className="flex items-center justify-end px-6 py-3 border-b flex-shrink-0"
          style={{
            backgroundColor: "var(--color-panel)",
            borderBottomColor: "var(--color-panel-border)",
          }}
        >
          {/* Search + New character button */}
          <div className="flex items-center gap-2">
            <SearchBox
              value={search.searchQuery}
              onChange={search.setSearchQuery}
              isSearching={search.isSearching}
              placeholder="Search cast..."
            />
            <Button
              variant="pink"
              type="button"
              onClick={handleOpenCastDialog}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring shadow-sm h-9 px-4"
              style={{
                borderRadius: "var(--radius-default)",
                gap: "6px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <Plus style={{ width: "16px", height: "16px" }} />
              New character
            </Button>
          </div>
        </div>

        {/* Cast Create Dialog */}
        <CastCreateDialog
          isOpen={isCastDialogOpen}
          onClose={handleCloseCastDialog}
        />
      </>
    );
  }

  // On cast detail page, render back button + cast name + type
  if (isCastDetail && selectedCast) {
    const typeColor = CAST_TYPE_COLORS[selectedCast.type];

    return (
      <div
        className="flex items-center px-6 py-3 border-b flex-shrink-0"
        style={{
          backgroundColor: "var(--color-panel)",
          borderBottomColor: "var(--color-panel-border)",
        }}
      >
        {/* Back button + Cast name + Type */}
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="utility"
            size="icon"
            onClick={handleBackToCast}
            aria-label="Back to cast"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-base font-semibold text-foreground truncate"
              style={{ fontSize: "16px" }}
            >
              {selectedCast.name}
            </span>
            <span className="text-muted-foreground/50 text-lg leading-none">
              &middot;
            </span>
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: typeColor }}
            >
              {selectedCast.type}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Collapsible
      open={search.isAdvancedOpen}
      onOpenChange={handleAdvancedToggle}
    >
      <div
        className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0 relative"
        style={{
          backgroundColor: "var(--color-panel)",
          borderBottomColor: "var(--color-panel-border)",
        }}
      >
        {/* Left: Sort dropdown + Filters button */}
        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <DropdownMenu open={isSortOpen} onOpenChange={handleSortOpenChange}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 shadow-sm h-9 px-3 border text-muted-foreground hover:text-foreground filter-btn ${isSortOpen ? "filter-btn--active" : ""}`}
                style={{
                  backgroundColor: isSortOpen
                    ? "rgba(var(--brand-pink-rgb), 0.12)"
                    : "var(--color-background)",
                  borderColor: isSortOpen ? "transparent" : undefined,
                  color: isSortOpen
                    ? "var(--color-primitive-neutral-0)"
                    : undefined,
                  borderRadius: "var(--radius-default)",
                  gap: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <ArrowUpDown style={{ width: "16px", height: "16px" }} />
                {SORT_OPTIONS.find(
                  (opt) =>
                    opt.value === search.sortBy &&
                    opt.order === search.sortOrder,
                )?.label ?? "Last Used"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={8}
              alignOffset={8}
              className="min-w-[275px]"
              style={{
                backgroundColor: "var(--color-panel)",
                border: "1px solid hsl(var(--border))",
                borderRadius: "var(--radius-default)",
                boxShadow:
                  "0 4px 6px rgba(var(--color-primitive-neutral-1000-rgb), 0.3)",
                padding: "4px",
              }}
            >
              {SORT_OPTIONS.map((option) => {
                const isSelected =
                  search.sortBy === option.value &&
                  search.sortOrder === option.order;
                return (
                  <DropdownMenuItem
                    key={`${option.value}-${option.order}`}
                    onClick={() => {
                      search.setSortBy(option.value);
                      search.setSortOrder(option.order as "asc" | "desc");
                    }}
                    className={`cursor-pointer rounded filter-menu-item ${isSelected ? "filter-menu-item--selected bg-[rgba(var(--brand-pink-rgb),0.12)] text-[var(--color-primitive-neutral-0)]" : "hover:bg-[rgba(var(--color-primitive-neutral-0-rgb),0.1)] focus:bg-[rgba(var(--color-primitive-neutral-0-rgb),0.1)]"}`}
                    style={{
                      fontSize: "13px",
                      padding: "8px 12px",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {option.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filters button */}
          <CollapsibleTrigger
            className={`inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 shadow-sm h-9 px-3 border text-muted-foreground hover:text-foreground filter-btn ${search.isAdvancedOpen ? "filter-btn--active" : ""}`}
            style={{
              backgroundColor: search.isAdvancedOpen
                ? "rgba(var(--brand-pink-rgb), 0.12)"
                : "var(--color-background)",
              borderColor: search.isAdvancedOpen ? "transparent" : undefined,
              color: search.isAdvancedOpen
                ? "var(--color-primitive-neutral-0)"
                : undefined,
              borderRadius: "var(--radius-default)",
              gap: "8px",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <SlidersHorizontal style={{ width: "16px", height: "16px" }} />
            Filters
            {search.hasActiveFilters && (
              <span
                className="ml-1 w-2 h-2 rounded-full"
                style={{ backgroundColor: "var(--color-primitive-pink-500)" }}
              />
            )}
          </CollapsibleTrigger>
        </div>

        {/* Right: Search + Add button */}
        <div className="flex items-center gap-2">
          <SearchBox
            value={search.searchQuery}
            onChange={search.setSearchQuery}
            isSearching={search.isSearching}
            placeholder={getPlaceholder()}
            onFocus={handleSearchFocus}
          />

          {/* Add button - show on workflows and shots pages */}
          {(isWorkflows || isShots) && (
            <Button
              variant="pink"
              type="button"
              onClick={handleAddClick}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring shadow-sm h-9 px-4"
              style={{
                borderRadius: "var(--radius-default)",
                gap: "6px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <Plus style={{ width: "16px", height: "16px" }} />
              {getAddButtonText()}
            </Button>
          )}
        </div>

        {/* Collapsible content: Advanced filter options - absolutely positioned */}
        <CollapsibleContent className="absolute top-full left-2 mt-2 z-50">
          <div
            className="p-4"
            style={{
              backgroundColor: "var(--color-panel)",
              border: "1px solid hsl(var(--border))",
              borderRadius: "var(--radius-default)",
              boxShadow:
                "0 4px 6px rgba(var(--color-primitive-neutral-1000-rgb), 0.3)",
            }}
          >
            <AdvancedFiltersContent
              filters={search.filters}
              selectedAttributes={search.selectedAttributes}
              onAttributesChange={search.setSelectedAttributes}
              selectedEntityTypes={search.selectedEntityTypes}
              onEntityTypesChange={search.setSelectedEntityTypes}
              dateFilter={search.dateFilter}
              onDateFilterChange={search.setDateFilter}
              sortBy={search.sortBy}
              onSortByChange={search.setSortBy}
              sortOrder={search.sortOrder}
              onSortOrderChange={search.setSortOrder}
              searchMode={search.searchMode}
              onSearchModeChange={search.setSearchMode}
              limit={search.limit}
              onLimitChange={search.setLimit}
              onClearFilters={search.clearFilters}
              hasActiveFilters={search.hasActiveFilters}
              isProjectScoped={true}
              total={search.total}
              visibleCount={search.results.length}
              isFetching={search.isFetching}
            />
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
